import {inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {HttpContext} from '@angular/common/http';
import {OrganisationService as BfaOrganisationService} from '../../bfa-api/services/organisation.service';
import {AUTH_TOKEN_HTTP_CONTEXT} from '../../constants/auth-token-http-context.constant';
import {OrganisationList} from '../../bfa-api/models/organisation-list';
import {lastValueFrom} from 'rxjs';
import {MeService as ApiMeService} from '../../api/services/me.service';
import {OrganisationError} from '../../errors/login-errors';
import {AuthService} from '../auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class OrganisationService {
    private readonly authService: AuthService = inject(AuthService);
    private readonly apiMeService: ApiMeService = inject(ApiMeService);
    private readonly bfaOrganisationService: BfaOrganisationService = inject(BfaOrganisationService);
    private readonly organisationList: WritableSignal<OrganisationList | undefined> = signal(undefined);
    private readonly lastOrganisationLoginError: WritableSignal<OrganisationError | undefined> = signal(undefined);

    public async getOrganisationList(): Promise<OrganisationList> {
        const token = await this.authService.getToken();
        return await lastValueFrom(this.bfaOrganisationService.organisationGetOrganisationList(undefined, new HttpContext().set(AUTH_TOKEN_HTTP_CONTEXT, token)));
    }

    public async fetchOrganisationList(): Promise<void> {
        try {
            const organisationList = await this.getOrganisationList();
            this.organisationList.set(organisationList);
        } catch (error) {
            this.organisationList.set(undefined);
            throw error;
        }
    }

    public resetOrganisationList(): void {
        this.organisationList.set(undefined);
    }

    public getOrganisationListSignal(): Signal<OrganisationList | undefined> {
        return this.organisationList.asReadonly();
    }

    public async setPreferredOrganisation(selectedOrganisationId: string): Promise<void> {
        const bearer = await this.authService.getBearer();
        await lastValueFrom(this.apiMeService.MeSetOrganisationForNextLogin({
            updateData: {
                organisationId: selectedOrganisationId
            },
            Authorization: bearer,
        }));
    }

    public setLastOrganisationLoginError(error: OrganisationError | undefined): void {
        this.lastOrganisationLoginError.set(error);
    }

    public getLastOrganisationLoginErrorSignal(): Signal<OrganisationError | undefined> {
        return this.lastOrganisationLoginError.asReadonly();
    }
}
